import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { techAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  ChevronLeft, Plus, X, Loader2, Search, Package, Star,
  FileText, DollarSign, Minus, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const EDITABLE_STATUSES = new Set(['draft', 'unscheduled', 'scheduled', 'in_progress']);

export default function TechEstimateEdit() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await techAPI.getEstimateDetail(estimateId);
      setEst(res.data);
    } catch {
      toast.error('Failed to load estimate');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [estimateId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (!est) return null;

  const editable = EDITABLE_STATUSES.has(est.status);

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="tech-estimate-edit">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={() => navigate(-1)} className="p-1 -ml-1" data-testid="estimate-back-btn">
            <ChevronLeft className="w-6 h-6 text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-950 truncate">{est.estimate_name || est.estimate_number}</p>
            <p className="text-[10px] text-slate-400">{est.estimate_number}</p>
          </div>
          <StatusBadge status={est.status} />
        </div>
      </div>

      <div className="max-w-lg mx-auto">
        {/* Customer info */}
        {est.customer_name && (
          <div className="px-4 py-3 bg-white border-b border-slate-100">
            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Customer</p>
            <p className="text-sm font-semibold text-slate-800 mt-0.5">{est.customer_name}</p>
            {est.customer_address && <p className="text-xs text-slate-500">{est.customer_address}</p>}
          </div>
        )}

        {/* Estimate header edit */}
        {editable && <EstimateHeaderEdit est={est} onUpdate={load} />}

        {/* Base items */}
        <ItemSection
          title="Line Items"
          items={est.items || []}
          estimateId={estimateId}
          optionId={null}
          editable={editable}
          onRefresh={load}
        />

        {/* Options */}
        {(est.options || []).map(opt => (
          <OptionBlock key={opt.id} option={opt} estimateId={estimateId} editable={editable} onRefresh={load} />
        ))}

        {/* Add option button */}
        {editable && <AddOptionButton estimateId={estimateId} onRefresh={load} />}

        {/* Totals */}
        <TotalsBlock est={est} />

        {!editable && (
          <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 font-semibold">This estimate is {est.status} and cannot be edited from the field.</p>
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── STATUS BADGE ──────────────────────────────────── */
function StatusBadge({ status }) {
  const colors = {
    draft: 'bg-slate-100 text-slate-600',
    unscheduled: 'bg-slate-100 text-slate-600',
    scheduled: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    sent: 'bg-purple-100 text-purple-700',
    presented: 'bg-purple-100 text-purple-700',
    awaiting_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-red-100 text-red-700',
  };
  return <Badge className={`text-[10px] ${colors[status] || 'bg-slate-100 text-slate-500'}`}>{status?.replace(/_/g, ' ')}</Badge>;
}


/* ─── HEADER EDIT ───────────────────────────────────── */
function EstimateHeaderEdit({ est, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(est.estimate_name || '');
  const [desc, setDesc] = useState(est.description || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await techAPI.updateEstimate(est.id, { estimate_name: name || undefined, description: desc || undefined });
      toast.success('Updated');
      setEditing(false);
      onUpdate();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="w-full px-4 py-3 text-left border-b border-slate-100 bg-white active:bg-slate-50" data-testid="edit-estimate-header">
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Description</p>
          <FileText className="w-3.5 h-3.5 text-slate-400" />
        </div>
        <p className="text-sm text-slate-600 mt-1">{est.description || 'Tap to add description...'}</p>
      </button>
    );
  }

  return (
    <div className="px-4 py-3 bg-white border-b border-slate-100 space-y-2">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Estimate name..."
        className="w-full h-10 px-3 text-sm font-semibold border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
        data-testid="estimate-name-input" />
      <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description / scope of work..."
        className="w-full h-20 px-3 py-2 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
        data-testid="estimate-desc-input" />
      <div className="flex gap-2">
        <button onClick={() => setEditing(false)} className="flex-1 h-9 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg">Cancel</button>
        <button onClick={save} disabled={saving} className="flex-1 h-9 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-40" data-testid="estimate-header-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save'}
        </button>
      </div>
    </div>
  );
}


/* ─── ITEM SECTION ──────────────────────────────────── */
function ItemSection({ title, items, estimateId, optionId, editable, onRefresh }) {
  const [adding, setAdding] = useState(false);

  return (
    <div className="bg-white border-b border-slate-100">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{title}</p>
        {items.length > 0 && <Badge className="bg-slate-100 text-slate-600 text-[10px]">{items.length}</Badge>}
      </div>

      {items.length > 0 && (
        <div className="px-4 space-y-1.5 pb-2">
          {items.map(item => (
            <ItemRow key={item.id} item={item} editable={editable} onRefresh={onRefresh} />
          ))}
        </div>
      )}

      {editable && (
        adding ? (
          <AddItemForm
            estimateId={estimateId}
            optionId={optionId}
            onDone={() => { setAdding(false); onRefresh(); }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <div className="px-4 pb-3">
            <button onClick={() => setAdding(true)}
              className="w-full flex items-center justify-center gap-2 h-10 border-2 border-dashed border-slate-300 rounded-lg text-sm font-bold text-slate-500 active:bg-slate-50"
              data-testid={`add-item-btn${optionId ? '-' + optionId : ''}`}>
              <Plus className="w-4 h-4" /> Add Line Item
            </button>
          </div>
        )
      )}
    </div>
  );
}


/* ─── ITEM ROW ──────────────────────────────────────── */
function ItemRow({ item, editable, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [qty, setQty] = useState(item.quantity);
  const [price, setPrice] = useState(item.unit_price);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await techAPI.updateEstimateItem(item.id, { name, quantity: qty, unit_price: price });
      setEditing(false);
      onRefresh();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await techAPI.deleteEstimateItem(item.id);
      toast.success('Item removed');
      onRefresh();
    } catch { toast.error('Failed to remove'); }
    finally { setDeleting(false); }
  };

  if (editing) {
    return (
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2" data-testid={`item-edit-${item.id}`}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Item name..."
          className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white" />
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-slate-400 font-bold">QTY</label>
            <div className="flex items-center gap-1.5 mt-0.5">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-8 h-8 flex items-center justify-center rounded border border-slate-300 bg-white"><Minus className="w-3 h-3" /></button>
              <input type="number" value={qty} onChange={e => setQty(Number(e.target.value) || 1)}
                className="w-14 h-8 text-center text-sm font-mono border border-slate-200 rounded bg-white" />
              <button onClick={() => setQty(q => q + 1)} className="w-8 h-8 flex items-center justify-center rounded border border-slate-300 bg-white"><Plus className="w-3 h-3" /></button>
            </div>
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-slate-400 font-bold">PRICE</label>
            <div className="flex items-center gap-1 mt-0.5 h-8 border border-slate-200 rounded bg-white px-2">
              <DollarSign className="w-3 h-3 text-slate-400" />
              <input type="number" step="0.01" value={price} onChange={e => setPrice(Number(e.target.value) || 0)}
                className="flex-1 h-full text-sm font-mono focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(false)} className="flex-1 h-9 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 h-9 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-40" data-testid={`item-save-${item.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Update'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-200 group" data-testid={`item-row-${item.id}`}>
      <button onClick={() => editable && setEditing(true)} className="flex-1 min-w-0 text-left" disabled={!editable}>
        <p className="text-sm font-medium text-slate-700 truncate">{item.name}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          <span className="font-mono">{item.quantity > 1 ? `${item.quantity} x ` : ''}</span>
          ${item.unit_price.toFixed(2)}
          {item.quantity > 1 && <span className="text-slate-500 font-semibold ml-2">= ${item.total_price.toFixed(2)}</span>}
        </p>
      </button>
      {editable && (
        <button onClick={remove} disabled={deleting} className="p-1.5 text-slate-300 hover:text-red-400 shrink-0" data-testid={`item-delete-${item.id}`}>
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}


/* ─── ADD ITEM FORM ─────────────────────────────────── */
function AddItemForm({ estimateId, optionId, onDone, onCancel }) {
  const [mode, setMode] = useState('search'); // 'search' | 'manual'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualName, setManualName] = useState('');
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (mode !== 'search' || query.length < 1) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await techAPI.searchPriceBook(query);
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [query, mode]);

  const addFromPriceBook = async (pb) => {
    setSubmitting(true);
    try {
      await techAPI.addEstimateItem(estimateId, {
        name: pb.name,
        description: pb.description || undefined,
        quantity: 1,
        unit_price: pb.unit_price,
        type: pb.item_type || 'service',
        taxable: pb.taxable !== false,
        option_id: optionId || undefined,
        price_book_item_id: pb.id,
      });
      toast.success('Item added');
      onDone();
    } catch { toast.error('Failed to add'); }
    finally { setSubmitting(false); }
  };

  const addManual = async () => {
    if (!manualName.trim()) return;
    setSubmitting(true);
    try {
      await techAPI.addEstimateItem(estimateId, {
        name: manualName.trim(),
        quantity: qty,
        unit_price: Number(price) || 0,
        type: 'service',
        taxable: true,
        option_id: optionId || undefined,
      });
      toast.success('Item added');
      onDone();
    } catch { toast.error('Failed to add'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="mx-4 mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid="add-item-form">
      {/* Mode tabs */}
      <div className="flex bg-slate-200 rounded-md p-0.5">
        <button onClick={() => setMode('search')}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-bold transition-colors ${mode === 'search' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
          <Search className="w-3 h-3" /> Price Book
        </button>
        <button onClick={() => setMode('manual')}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-bold transition-colors ${mode === 'manual' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
          <FileText className="w-3 h-3" /> Manual
        </button>
      </div>

      {mode === 'search' && (
        <>
          <div className="flex items-center gap-2 h-10 border-2 border-blue-300 rounded-lg bg-white overflow-hidden">
            <Search className="w-4 h-4 text-slate-400 ml-3" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search price book..."
              className="flex-1 h-full text-sm focus:outline-none" autoFocus data-testid="estimate-pb-search" />
            <button onClick={onCancel} className="px-2 text-slate-400"><X className="w-4 h-4" /></button>
          </div>
          {searching && <div className="flex items-center gap-2 py-1"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /><span className="text-xs text-slate-400">Searching...</span></div>}
          {results.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
              {results.map(r => (
                <button key={r.id} onClick={() => addFromPriceBook(r)} disabled={submitting}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left border-b border-slate-100 last:border-0 active:bg-blue-50"
                  data-testid={`pb-result-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                    <p className="text-[10px] text-slate-400">{r.item_type}{r.sku ? ` · ${r.sku}` : ''}</p>
                  </div>
                  <span className="text-sm font-mono font-bold text-slate-700 ml-2">${r.unit_price.toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
          {query.length > 0 && !searching && results.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-1">No items found</p>
          )}
          <button onClick={() => setMode('manual')}
            className="w-full flex items-center justify-center gap-1.5 h-8 text-xs font-bold text-blue-600 border border-dashed border-blue-300 rounded-lg active:bg-blue-50">
            <Plus className="w-3 h-3" /> Add Custom Line Item
          </button>
        </>
      )}

      {mode === 'manual' && (
        <>
          <input value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Item name..."
            className="w-full h-10 px-3 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white"
            autoFocus data-testid="estimate-manual-name" />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-slate-400 font-bold">QTY</label>
              <div className="flex items-center gap-1.5 mt-0.5">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-8 h-8 flex items-center justify-center rounded border border-slate-300 bg-white"><Minus className="w-3 h-3" /></button>
                <span className="w-8 text-center font-mono font-bold text-slate-950">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="w-8 h-8 flex items-center justify-center rounded border border-slate-300 bg-white"><Plus className="w-3 h-3" /></button>
              </div>
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-slate-400 font-bold">PRICE</label>
              <div className="flex items-center gap-1 mt-0.5 h-8 border-2 border-slate-200 rounded-lg bg-white px-2">
                <DollarSign className="w-3 h-3 text-slate-400" />
                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00"
                  className="flex-1 h-full text-sm font-mono focus:outline-none" data-testid="estimate-manual-price" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="flex-1 h-10 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg">Cancel</button>
            <button onClick={addManual} disabled={submitting || !manualName.trim()}
              className="flex-1 h-10 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-40"
              data-testid="estimate-manual-add">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Add Item'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


/* ─── OPTION BLOCK ──────────────────────────────────── */
function OptionBlock({ option, estimateId, editable, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const removeOption = async () => {
    if (!window.confirm(`Delete option "${option.name}" and all its items?`)) return;
    setDeleting(true);
    try {
      await techAPI.deleteEstimateOption(option.id);
      toast.success('Option removed');
      onRefresh();
    } catch { toast.error('Failed to remove option'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="border-t-2 border-slate-100 bg-white" data-testid={`option-block-${option.id}`}>
      <div className="w-full flex items-center gap-2 px-4 py-3 active:bg-slate-50 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {option.is_recommended && <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />}
        <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex-1 text-left truncate">{option.name}</span>
        {option.total > 0 && <span className="text-xs font-mono font-bold text-slate-600">${option.total.toFixed(2)}</span>}
        {editable && (
          <button onClick={e => { e.stopPropagation(); removeOption(); }} disabled={deleting}
            className="p-1 text-slate-300 hover:text-red-400"
            data-testid={`option-delete-${option.id}`}>
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>

      {expanded && (
        <>
          {option.recommendation_reason && (
            <div className="mx-4 mb-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-[10px] text-amber-700"><span className="font-bold">Recommended:</span> {option.recommendation_reason}</p>
            </div>
          )}
          <ItemSection
            title={`${option.name} Items`}
            items={option.items || []}
            estimateId={estimateId}
            optionId={option.id}
            editable={editable}
            onRefresh={onRefresh}
          />
        </>
      )}
    </div>
  );
}


/* ─── ADD OPTION BUTTON ─────────────────────────────── */
function AddOptionButton({ estimateId, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [recommended, setRecommended] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await techAPI.addEstimateOption(estimateId, {
        name: name.trim(),
        is_recommended: recommended,
        recommendation_reason: reason || undefined,
      });
      toast.success('Option created');
      setName(''); setRecommended(false); setReason('');
      setAdding(false);
      onRefresh();
    } catch { toast.error('Failed to create option'); }
    finally { setSubmitting(false); }
  };

  if (!adding) {
    return (
      <div className="px-4 py-3">
        <button onClick={() => setAdding(true)}
          className="w-full flex items-center justify-center gap-2 h-10 border-2 border-dashed border-blue-300 rounded-lg text-sm font-bold text-blue-600 active:bg-blue-50"
          data-testid="add-option-btn">
          <Plus className="w-4 h-4" /> Add Option
        </button>
      </div>
    );
  }

  return (
    <div className="mx-4 my-3 p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2" data-testid="add-option-form">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Option name (e.g., Premium Package)..."
        className="w-full h-10 px-3 text-sm font-semibold border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white"
        autoFocus data-testid="option-name-input" />
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={recommended} onChange={e => setRecommended(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
        <Star className={`w-4 h-4 ${recommended ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
        <span className="text-xs font-bold text-slate-600">Recommended option</span>
      </label>
      {recommended && (
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why recommend? (optional)"
          className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none bg-white" />
      )}
      <div className="flex gap-2">
        <button onClick={() => setAdding(false)} className="flex-1 h-10 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg">Cancel</button>
        <button onClick={submit} disabled={submitting || !name.trim()}
          className="flex-1 h-10 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-40"
          data-testid="option-save-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create Option'}
        </button>
      </div>
    </div>
  );
}


/* ─── TOTALS BLOCK ──────────────────────────────────── */
function TotalsBlock({ est }) {
  return (
    <div className="mx-4 mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="estimate-totals">
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm text-slate-600">
          <span>Subtotal</span>
          <span className="font-mono">${est.subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-600">
          <span>Tax{est.tax_rate ? ` (${est.tax_rate}%)` : ''}</span>
          <span className="font-mono">${est.tax_amount.toFixed(2)}</span>
        </div>
        <div className="border-t border-slate-200 pt-2 flex justify-between">
          <span className="text-base font-bold text-slate-950">Total</span>
          <span className="text-base font-mono font-bold text-slate-950">${est.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
